function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
/* eslint-disable linebreak-style */
// ----------------------------------------------------------------------------
// Component: iam-cart-dropdown
// ----------------------------------------------------------------------------
// The iamCartDropdown class provides a custom element for displaying the cart dropdown.
// It uses the iamotaEventHandler to listen to cart updates and updates the dropdown content accordingly.
// It leverages the iamota-shopify-ajax library for fetching cart data and applies loading state management.

// -------------------------------------
// Dependencies
// -------------------------------------

// Scripts
import 'snippets/cart-icon/cart-icon';
import 'snippets/cart-dropdown/cart-dropdown';
import 'snippets/card/card';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { sectionRendering } from 'scripts/helpers/iamota-shopify-ajax';

// Styles
import './_cart-dropdown.scss';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCartDropdown extends HTMLElement {
  connectedCallback() {
    var _this$parentWrapper, _this$parentWrapper2, _this$parentWrapper3, _this$parentWrapper4, _this$parentWrapper5, _this$parentWrapper6;
    // Variables
    this.itemsList = this.querySelector('.cart-dropdown__items');
    this.footer = this.querySelector('.cart-dropdown__footer');
    this.parentWrapper = this.closest('.cart-icon--dropdown');
    this.cartLink = (_this$parentWrapper = this.parentWrapper) === null || _this$parentWrapper === void 0 ? void 0 : _this$parentWrapper.querySelector('.cart-dropdown__header-link');
    this.hideTimeoutId = null;

    // Subscribe to native events
    (_this$parentWrapper2 = this.parentWrapper) === null || _this$parentWrapper2 === void 0 || _this$parentWrapper2.addEventListener('focusin', this.focusin.bind(this));
    (_this$parentWrapper3 = this.parentWrapper) === null || _this$parentWrapper3 === void 0 || _this$parentWrapper3.addEventListener('focusout', this.focusout.bind(this));
    (_this$parentWrapper4 = this.parentWrapper) === null || _this$parentWrapper4 === void 0 || _this$parentWrapper4.addEventListener('keyup', this.keyup.bind(this));

    // Hover handlers with delay-on-hide so the dropdown doesn't disappear
    // while the cursor briefly leaves the parent's bounding box (e.g., the
    // gap between the cart icon and the absolutely positioned dropdown).
    (_this$parentWrapper5 = this.parentWrapper) === null || _this$parentWrapper5 === void 0 || _this$parentWrapper5.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    (_this$parentWrapper6 = this.parentWrapper) === null || _this$parentWrapper6 === void 0 || _this$parentWrapper6.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // Subscribe to custom events
    iamotaEventHandler.subscribe('cart:modifying:data', this.onCartModifyingData.bind(this), {
      source: "cart-dropdown.js"
    });
    iamotaEventHandler.subscribe('cart:modified', this.onCartModified.bind(this), {
      source: "cart-dropdown.js"
    });

    // Fetch and update the cart dropdown on initialization
    this.forceUpdate();
  }
  disconnectedCallback() {
    clearTimeout(this.hideTimeoutId);
  }

  /**
   * Open the dropdown on mouse enter, cancelling any pending hide.
   */
  onMouseEnter() {
    var _this$parentWrapper7;
    clearTimeout(this.hideTimeoutId);
    (_this$parentWrapper7 = this.parentWrapper) === null || _this$parentWrapper7 === void 0 || _this$parentWrapper7.classList.add('--open');
  }

  /**
   * Start a 300ms timer before closing on mouse leave. Re-entering within
   * the grace period (handled by onMouseEnter) cancels the close.
   */
  onMouseLeave() {
    clearTimeout(this.hideTimeoutId);
    this.hideTimeoutId = setTimeout(() => {
      var _this$parentWrapper8;
      (_this$parentWrapper8 = this.parentWrapper) === null || _this$parentWrapper8 === void 0 || _this$parentWrapper8.classList.remove('--open');
    }, 300);
  }

  /**
   * Cart is Modifying, ask for the sections we need to be included to avoid an extra ajax call
   */
  onCartModifyingData(request) {
    var _request$sections;
    this.setLoading(true);
    var sections = new Set((_request$sections = request.sections) === null || _request$sections === void 0 ? void 0 : _request$sections.split(',')); // Existing sections requested
    sections.add('cart-dropdown__items');
    sections.add('cart-dropdown__footer');
    request.sections = Array.from(sections).join(',');
    return request;
  }

  /**
   * Fetches the updated cart dropdown content and updates the DOM.
   */
  onCartModified(cart) {
    if (cart !== null && cart !== void 0 && cart.sections && cart.sections['cart-dropdown__items'] && cart.sections['cart-dropdown__footer']) {
      this.updateSections(cart.sections['cart-dropdown__items'], cart.sections['cart-dropdown__footer']);
    } else {
      this.forceUpdate();
    }
  }

  /**
   * Fetches the updated cart dropdown content and updates the DOM.
   */
  updateSections(itemsHTML, footerHTML) {
    var _this = this;
    return _asyncToGenerator(function* () {
      _this.itemsList.innerHTML = yield iamotaEventHandler.applyFilters('cart:dropdown:items:updating', itemsHTML);
      _this.footer.innerHTML = yield iamotaEventHandler.applyFilters('cart:dropdown:footer:updating', footerHTML);
      iamotaEventHandler.trigger('cart:dropdown:updated', {
        iamCartDropdown: _this,
        itemsHTML,
        footerHTML
      }, {
        context: _this
      });
      _this.setLoading(false);
    })();
  }

  /**
   * Fetch the latest cart state
   */
  forceUpdate() {
    this.setLoading(true);
    sectionRendering.fetch(['cart-dropdown__items', 'cart-dropdown__footer']).then(sectionContent => {
      this.updateSections(sectionContent['cart-dropdown__items'], sectionContent['cart-dropdown__footer']);
    });
  }

  /**
   * Toggles the loading state by adding or removing the --loading modifier class.
   *
   * @param {boolean} isLoading - Whether to show the loading state.
   */
  setLoading(isLoading) {
    this.classList.toggle('--loading', isLoading);
  }

  /**
   * Handles the focusin event.
   * Adds the '--open' class and updates tabindex values.
   */
  focusin() {
    var _this$parentWrapper9;
    if (!((_this$parentWrapper9 = this.parentWrapper) !== null && _this$parentWrapper9 !== void 0 && _this$parentWrapper9.classList.contains('--open'))) {
      this.parentWrapper.classList.add('--open');
      var tabIndexItems = this.querySelectorAll('[tabindex="-1"]');
      tabIndexItems.forEach(tabIndexItem => {
        tabIndexItem.setAttribute('tabindex', '0');
      });
    }
  }

  /**
   * Handles the focusout event.
   * Removes the '--open' class.
   */
  focusout() {
    var _this$parentWrapper0;
    if ((_this$parentWrapper0 = this.parentWrapper) !== null && _this$parentWrapper0 !== void 0 && _this$parentWrapper0.classList.contains('--open')) {
      this.parentWrapper.classList.remove('--open');
    }
  }

  /**
   * Handles the keyup event.
   * Closes the dropdown when the 'Escape' key is pressed.
   */
  keyup(e) {
    var _this$parentWrapper1;
    if (e.key === 'Escape' && (_this$parentWrapper1 = this.parentWrapper) !== null && _this$parentWrapper1 !== void 0 && _this$parentWrapper1.classList.contains('--open')) {
      var _this$cartLink;
      (_this$cartLink = this.cartLink) === null || _this$cartLink === void 0 || _this$cartLink.focus();
      this.parentWrapper.classList.remove('--open');
    }
  }
}

// Define the custom element
customElements.define('iam-cart-dropdown', iamCartDropdown);